import { useCallback, useMemo, useRef, useState } from 'react'
import {
  SprintReport,
  SprintReportGroupKey,
  getSprintReport,
  toApiSprintReportGroupKey,
} from '../../../../lib/functions/sprintReport'
import { SprintReportRow } from '../model/sprintReportRow'
import {
  OrganizationWorkingDayCalendarDetail,
  getOrganizationWorkingDayCalendar,
} from '../../../../lib/functions/organizationWorkingDayCalendar'
import { SprintReportSearchCondition } from './searchCondition'
import { AggregateTarget } from '../../../../domain/entity/WbsItemEntity'
import { dateV2Service } from '../../../../domain/value-object/DateV2'

type SprintReportRefreshOptions = {
  // 明示的に再取得したい場合 (検索ボタン / リロードボタン) は true を渡し、キャッシュを無視する。
  force?: boolean
}

type SprintReportDataApi = {
  rowData: SprintReportRow[]
  workingDayCalendars: OrganizationWorkingDayCalendarDetail[]
  loading: boolean
  refresh: (
    projectUuid: string,
    searchCondition: SprintReportSearchCondition,
    aggregateTarget: AggregateTarget,
    options?: SprintReportRefreshOptions
  ) => Promise<void>
}

// API リクエストパラメータが同一の場合、再度叩かずに直近の結果を再利用するためのキャッシュ。
// 集計対象 (タスク / 成果物) 切替の往復で同じ結果に戻る場合などに API コール / ローディング表示を省略する。
type CacheEntry = {
  rowData: SprintReportRow[]
  workingDayCalendars: OrganizationWorkingDayCalendarDetail[]
}

// 集計対象切替やパンくずの往復で何度か行き来する程度の用途を想定。
// プロジェクト/WBS/チーム/期間/集計対象すべて分けるとキー空間は広いため、
// 上限を設けてメモリ膨張を防ぐ。8 件あれば主要な往復パターンは概ねカバーできる。
const CACHE_MAX_ENTRIES = 8

// JSON.stringify はオブジェクトのキー順に依存して文字列が変わる。
// 将来 searchCondition の構造が変わっても挙動が壊れないよう、明示的にプリミティブを連結する。
// SPRINT モード時は dateTerm 系を、日付モード時は sprintUuid 系を、それぞれ実際に送信しない
// 値はキャッシュキーから外して、不要なキャッシュミスを防ぐ。
const buildCacheKey = (
  projectUuid: string,
  searchCondition: SprintReportSearchCondition,
  aggregateTarget: AggregateTarget
): string => {
  const { dateTerm } = searchCondition
  const isSprintMode = searchCondition.groupKey === SprintReportGroupKey.SPRINT
  return [
    projectUuid,
    searchCondition.rootWbsItemUuid ?? '',
    searchCondition.teamUuid ?? '',
    searchCondition.groupKey,
    isSprintMode ? '' : (dateTerm.startDate ?? ''),
    isSprintMode ? '' : (dateTerm.endDate ?? ''),
    isSprintMode ? (searchCondition.startSprintUuid ?? '') : '',
    isSprintMode ? (searchCondition.endSprintUuid ?? '') : '',
    aggregateTarget,
  ].join('|')
}

const toApiDateString = (timestamp: number | undefined): string | undefined => {
  const date = dateV2Service.fromNumber(timestamp)
  return date ? dateV2Service.fromVoToApiRequest(date) : undefined
}

const toDate = (timestamp: number | undefined): Date | undefined =>
  dateV2Service.fromNumber(timestamp)

// BulkSheetView の getRowId は data.uuid を参照するが、API レスポンスには uuid がないため
// (member.uuid, key) で安定な行IDを生成する。同じ ID が複数行に返ると ag-grid が行を取りこぼし先頭行が消える。
// rowNumber はフィルタ前の全行での位置を保持し、フィルタ後も変わらない。
const toRow = (response: SprintReport, index: number): SprintReportRow => ({
  ...response,
  uuid: `${response.member.uuid}-${response.key}`,
  rowNumber: index + 1,
})

export const useSprintReportData = (): SprintReportDataApi => {
  const [rowData, setRowData] = useState<SprintReportRow[]>([])
  const [workingDayCalendars, setWorkingDayCalendars] = useState<
    OrganizationWorkingDayCalendarDetail[]
  >([])
  const [loading, setLoading] = useState<boolean>(false)
  // 検索ボタン連打で複数の refresh が並走したとき、遅いレスポンスが速いレスポンスを
  // 上書きして古いデータを表示してしまう race を避けるため、リクエストごとに ID を振って
  // 最新でないレスポンスは破棄する。
  const requestIdRef = useRef(0)
  const cacheRef = useRef<Map<string, CacheEntry>>(new Map())

  const refresh = useCallback(
    async (
      projectUuid: string,
      searchCondition: SprintReportSearchCondition,
      aggregateTarget: AggregateTarget,
      options?: SprintReportRefreshOptions
    ) => {
      const requestId = ++requestIdRef.current
      const { dateTerm } = searchCondition
      const isSprintMode =
        searchCondition.groupKey === SprintReportGroupKey.SPRINT
      // SPRINT モード時はスプリント (開始 or 終了) のいずれか指定が必要。
      // 日付モード時は開始日と終了日の両方必須 (片方欠けた状態での検索は無効)。
      const hasRequiredFilter = isSprintMode
        ? !!searchCondition.startSprintUuid || !!searchCondition.endSprintUuid
        : !!dateTerm.startDate && !!dateTerm.endDate
      if (!hasRequiredFilter) {
        setRowData([])
        setWorkingDayCalendars([])
        return
      }
      const cacheKey = buildCacheKey(
        projectUuid,
        searchCondition,
        aggregateTarget
      )
      if (!options?.force) {
        const cached = cacheRef.current.get(cacheKey)
        if (cached) {
          // キャッシュヒット時は API を叩かず即座に既存値を流し込む。ローディング表示も出さない。
          // LRU 順を維持するため、ヒットしたエントリは最近使った位置に並び替える (Map は挿入順を保持する)。
          cacheRef.current.delete(cacheKey)
          cacheRef.current.set(cacheKey, cached)
          setRowData(cached.rowData)
          setWorkingDayCalendars(cached.workingDayCalendars)
          return
        }
      }
      setLoading(true)
      try {
        // バックエンドは groupKey に SPRINT という値を知らないので、API リクエスト型側で
        // SPRINT を除外した ApiSprintReportGroupKey に変換して送信する (toApiSprintReportGroupKey)。
        // バックエンド側は startSprintUuid / endSprintUuid の有無でスプリント列モードに
        // 自動切替するため、UI 上の SPRINT 選択は sprintUuid 送信で表現する。
        const [reportResponse, calendarResponse] = await Promise.all([
          getSprintReport({
            projectUuid,
            rootWbsItemUuid: searchCondition.rootWbsItemUuid,
            teamUuid: searchCondition.teamUuid,
            groupKey: toApiSprintReportGroupKey(searchCondition.groupKey),
            // SPRINT モード時は dateTerm を送らない (バックエンドが意図しない日付フィルタを掛けないように)。
            startDate: isSprintMode
              ? undefined
              : toApiDateString(dateTerm.startDate),
            endDate: isSprintMode
              ? undefined
              : toApiDateString(dateTerm.endDate),
            // 日付モード時は sprintUuid を送らない (バックエンドが SPRINT 列モードに誤って入らないように)。
            startSprintUuid: isSprintMode
              ? searchCondition.startSprintUuid
              : undefined,
            endSprintUuid: isSprintMode
              ? searchCondition.endSprintUuid
              : undefined,
            aggregateTargetType: aggregateTarget,
          }),
          getOrganizationWorkingDayCalendar({
            startDate: isSprintMode ? undefined : toDate(dateTerm.startDate),
            endDate: isSprintMode ? undefined : toDate(dateTerm.endDate),
          }),
        ])
        if (requestId !== requestIdRef.current) return
        const newRowData = (reportResponse.json ?? []).map(toRow)
        const newCalendars = calendarResponse.json ?? []
        setRowData(newRowData)
        setWorkingDayCalendars(newCalendars)
        // force リフレッシュも含め、最新の取得結果でキャッシュを上書きしておく。
        // 既存キーがあれば一旦消してから set し直し、最新使用位置に並べ替える (LRU)。
        cacheRef.current.delete(cacheKey)
        cacheRef.current.set(cacheKey, {
          rowData: newRowData,
          workingDayCalendars: newCalendars,
        })
        // 上限を超えたら最古のエントリから破棄する。
        while (cacheRef.current.size > CACHE_MAX_ENTRIES) {
          const oldestKey = cacheRef.current.keys().next().value
          if (oldestKey === undefined) break
          cacheRef.current.delete(oldestKey)
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false)
        }
      }
    },
    []
  )

  return useMemo(
    () => ({
      rowData,
      workingDayCalendars,
      loading,
      refresh,
    }),
    [rowData, workingDayCalendars, loading, refresh]
  )
}
